import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import Sidenav from "../components/Sidenav";
import CreateGenuineProduct from "../components/CreateGenuineProduct";
import EditGenuineProduct from "../components/EditGenuineProduct";
import DeleteProductModal from "../components/DeleteProductModal";
import "../Console.css";

/* ------------------ Styles (no "ad" in class names) ------------------ */
const styles = (
  <style>{`
    .toolbar { display:flex; align-items:center; justify-content:space-between; margin:0 0 16px 0; }
    .count-badge {
      display:inline-flex; align-items:center; gap:7px;
      background:#fff; border:1px solid #e5e7eb; color:#111827;
      font-size:12px; font-weight:800; padding:7px 13px; border-radius:9999px;
      box-shadow:0 1px 3px rgba(0,0,0,0.05);
    }

    .card-grid { display:grid; gap:14px; grid-template-columns:repeat(3,minmax(0,1fr)); }
    @media (max-width:1200px) { .card-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
    @media (max-width:760px)  { .card-grid { grid-template-columns:1fr; } }

    .gp-card {
      background:#fff; border:1px solid #e5e7eb; border-radius:16px;
      padding:16px; box-shadow:0 1px 3px rgba(0,0,0,0.04),0 8px 24px rgba(0,0,0,0.05);
      color:#111827; font-size:13px; line-height:1.4; position:relative;
      transition:box-shadow 0.15s, transform 0.15s;
    }
    .gp-card:hover {
      box-shadow:0 4px 16px rgba(0,0,0,0.08),0 12px 32px rgba(0,0,0,0.07);
      transform:translateY(-1px);
    }

    .gp-header { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:12px; }
    .gp-left   { display:flex; align-items:center; gap:10px; min-width:0; }

    .brand-avatar {
      width:44px; height:44px; border-radius:12px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center;
      font-weight:800; font-size:14px; color:#fff; text-transform:uppercase;
      border:1px solid rgba(0,0,0,0.08);
    }
    .brand-name { font-weight:800; font-size:15px; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .ref-pill {
      display:inline-flex; align-items:center; gap:4px; padding:2px 7px;
      border-radius:9999px; background:#f3f4f6; color:#6b7280;
      font-size:10px; font-weight:700; white-space:nowrap; margin-top:3px;
    }

    .price-pill {
      display:inline-flex; align-items:center; gap:6px; padding:5px 12px;
      border-radius:9999px;
      background:linear-gradient(135deg,#fff7f3 0%,#fff3ee 100%);
      border:1.5px solid rgba(255,81,0,0.2);
      color:#c13d00; font-size:12px; font-weight:800;
    }

    .divider { height:1px; background:#f3f4f6; margin:12px 0; }

    .gallery { display:flex; flex-wrap:wrap; gap:7px; margin-bottom:12px; }
    .gallery a {
      display:block; width:76px; height:76px; border-radius:10px; overflow:hidden;
      background:#f3f4f6; border:1px solid #e5e7eb; position:relative; flex-shrink:0;
      transition:box-shadow 0.15s;
    }
    .gallery a:hover { box-shadow:0 4px 12px rgba(0,0,0,0.12); }
    .gallery img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.2s; }
    .gallery a:hover img { transform:scale(1.06); }

    .desc { color:#374151; font-size:13px; line-height:1.65; margin-bottom:4px; }

    .meta-stack { display:flex; flex-direction:column; gap:7px; }
    .meta-item  { display:flex; align-items:baseline; gap:8px; font-size:12.5px; }
    .meta-icon-label {
      display:inline-flex; align-items:center; gap:5px;
      font-weight:700; color:#9ca3af; font-size:11px; min-width:82px; flex-shrink:0;
    }
    .meta-icon-label i { font-size:11px; width:12px; text-align:center; }
    .meta-content { color:#111827; overflow-wrap:anywhere; }

    .empty-state { background:#fff; border:1px dashed #e5e7eb; border-radius:12px; padding:32px; text-align:center; color:#6b7280; font-size:13px; }

    .card-actions { position:relative; }
    .icon-ellipsis {
      display:inline-flex; align-items:center; justify-content:center;
      width:34px; height:34px; border-radius:9px;
      background:#f9fafb; color:#111827; border:1px solid #e5e7eb; font-size:13px;
    }
    .icon-ellipsis:hover { background:#f3f4f6; }
    .icon-ellipsis:focus { outline:2px solid #d1d5db; }

    .menu-pop {
      position:absolute; top:38px; right:0;
      background:#fff; border:1px solid #e5e7eb; border-radius:12px;
      box-shadow:0 10px 30px rgba(0,0,0,0.08);
      min-width:180px; padding:6px; z-index:50;
    }
    .menu-item {
      width:100%; display:flex; align-items:center; gap:10px;
      border:none; background:transparent; text-align:left;
      padding:8px 10px; border-radius:8px; font-size:13px; color:#111827; font-weight:700;
    }
    .menu-item i { font-size:14px; }
    .menu-item:hover { background:#f3f4f6; }
    .menu-item.danger { color:#b91c1c; }
    .menu-item.danger:hover { background:#fff1f2; }
    .menu-item:disabled { opacity:.6; cursor:not-allowed; }
  `}</style>
);

/* ------------------ Tiny helpers ------------------ */
function formatDate(dateTimeString) {
  return new Date(dateTimeString).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
}

function ensureHttp(url) {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function renderContact(contact = {}) {
  const parts = [];
  if (contact.phone) {
    parts.push(
      <div key="phone">
        <i className="fa fa-phone" style={{ marginRight: 6 }} />
        <a href={`tel:${contact.phone}`}>{contact.phone}</a>
      </div>
    );
  }
  if (contact.whatsapp) {
    parts.push(
      <div key="wa">
        <i className="fab fa-whatsapp" style={{ marginRight: 6, color: "green" }} />
        <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
          {contact.whatsapp}
        </a>
      </div>
    );
  }
  if (contact.email) {
    parts.push(
      <div key="email">
        <i className="fa fa-envelope" style={{ marginRight: 6, color: "#d44638" }} />
        <a href={`mailto:${contact.email}`}>{contact.email}</a>
      </div>
    );
  }
  if (contact.website) {
    const safe = ensureHttp(contact.website);
    parts.push(
      <div key="web">
        <i className="fa fa-globe" style={{ marginRight: 6, color: "#007bff" }} />
        <a href={safe} target="_blank" rel="noopener noreferrer">
          {contact.website}
        </a>
      </div>
    );
  }
  return parts.length ? parts : null;
}

function getBrandInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function getBrandColor(str) {
  const palette = ["#4f46e5","#0ea5e9","#22c55e","#f59e0b","#ef4444","#a855f7","#14b8a6","#f97316"];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

/* ------------------ Component ------------------ */
const BuyGenuine = ({ domain, userData }) => {
  const [genuineProducts, setGenuineProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [userDataState, setUserDataState] = useState(userData);

  const [
    userId,
    userType,
    userName,
    userEmail,
    userPassword,
    userOrg,
    userPermissions,
    userAnonymous,
  ] = userDataState || [];

  useEffect(() => {
    setUserDataState(userData);
  }, [userData]);

  const ready = useMemo(() => {
    if (!userType) return false;
    if (userType === "brandadmin" && !userOrg) return false;
    return true;
  }, [userType, userOrg]);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [showCreateGenuineProductModal, setShowCreateGenuineProductModal] = useState(false);
  const [showEditGenuineProductModal, setShowEditGenuineProductModal] = useState(false);
  const [editProductId, setEditProductId] = useState("");

  const handleCloseCreateGenuineProductModal = () => setShowCreateGenuineProductModal(false);
  const handleShowCreateGenuineProductModal = () => setShowCreateGenuineProductModal(true);

  const handleCloseEditGenuineProductModal = () => {
    setShowEditGenuineProductModal(false);
    setEditProductId("");
  };

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const fetchGenuineProducts = async () => {
    setLoading(true);
    setErr("");
    try {
      const url =
        userType === "brandadmin"
          ? `/api/genuineProducts/by-client/${encodeURIComponent(String(userOrg || "").trim())}`
          : `/api/genuineProducts`;

      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setGenuineProducts(list);
    } catch (e) {
      console.error("Error fetching products:", e);
      setErr("Failed to load items.");
      setGenuineProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready) return;
    fetchGenuineProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, userType, userOrg]);

  const handleFormSubmit = () => {
    if (ready) fetchGenuineProducts();
  };

  const handleDeleteProduct = (product) => {
    setOpenMenuId(null);
    setDeleteProduct({ _id: product._id, brand: product.brand });
    setShowDeleteModal(true);
  };

  const handleEditProduct = (id) => {
    setOpenMenuId(null);
    setEditProductId(id);
    setShowEditGenuineProductModal(true);
  };

  useEffect(() => {
    try {
      if (typeof Fancybox?.bind === "function") {
        Fancybox.bind("[data-fancybox]", {});
      }
    } catch (e) {
      console.warn("Fancybox bind failed:", e);
    }
    return () => {
      try {
        if (typeof Fancybox?.destroy === "function") {
          Fancybox.destroy();
        }
      } catch {}
    };
  }, [genuineProducts]);

  const count = Array.isArray(genuineProducts) ? genuineProducts.length : 0;

  return (
    <>
      <Helmet>
        <title>Buy Genuine | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services."
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div className="col" style={{ padding: 10 }}>
                <b style={{ fontSize: "28px", textShadow: "#1c1c1c 1px 2px 0px" }}>
                  <i className="fas fa-shield" /> Buy Genuine
                </b>
                <p style={{ paddingTop: 8, textShadow: "#1c1c1c 1px 1px 0px", marginBottom: 14 }}>
                  Manage genuine products
                </p>
                <button
                  className="btn btn-light btn-lg"
                  style={{
                    borderRadius: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                    paddingBlock: "10px",
                    paddingInline: "30px",
                  }}
                  onClick={handleShowCreateGenuineProductModal}
                >
                  <span style={{ marginRight: 10 }}>
                    <i className="fa-solid fa-plus-circle" />
                  </span>
                  <b>Add genuine product</b>
                </button>
              </div>
            </div>
          </header>

          {/* Cards */}
          <div
            className="p-4"
            style={{ backgroundColor: "#ebebeb", position: "relative", zIndex: 1 }}
          >
            <div className="toolbar">
              <div className="count-badge" title="Total items">
                <i className="fas fa-th" />
                Total: {count}
              </div>

              {!ready ? (
                <span style={{ color: "#6b7280", fontSize: 12 }}>Loading user…</span>
              ) : loading ? (
                <span style={{ color: "#6b7280", fontSize: 12 }}>Loading…</span>
              ) : err ? (
                <span style={{ color: "#b91c1c", fontSize: 12 }}>{err}</span>
              ) : null}
            </div>

            {!ready ? (
              <div className="empty-state">Loading user…</div>
            ) : count === 0 && !loading ? (
              <div className="empty-state">No genuine products yet.</div>
            ) : (
              <div className="card-grid" role="list">
                {genuineProducts.map((product, idx) => {
                  const id = product?._id || String(idx);
                  const menuOpen = openMenuId === id;

                  const brandName = product?.brand || "—";
                  const brandColor = getBrandColor(brandName);
                  const brandInitials = getBrandInitials(brandName);
                  const refShort = product?._id?.slice(-6) || "—";
                  const price = product?.price ? `${product.price} UGX` : null;

                  const allImages = (
                    Array.isArray(product?.imageUrls) && product.imageUrls.length
                      ? product.imageUrls
                      : Array.isArray(product?.images)
                      ? product.images
                      : []
                  ).filter((u) => typeof u === "string" && /^https?:\/\//i.test(u));

                  const GALLERY_MAX = 4;
                  const visibleImages = allImages.slice(0, GALLERY_MAX);
                  const hiddenCount = Math.max(0, allImages.length - GALLERY_MAX);

                  const contact = renderContact(product?.contact);

                  return (
                    <article
                      key={product?._id || idx}
                      className="gp-card"
                      role="listitem"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Header */}
                      <div className="gp-header">
                        <div className="gp-left">
                          <div className="brand-avatar" style={{ background: brandColor }}>
                            {brandInitials}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="brand-name" title={brandName}>{brandName}</div>
                            <span className="ref-pill">
                              <i className="fas fa-hashtag" style={{ fontSize: 9 }} />
                              {refShort}
                            </span>
                          </div>
                        </div>

                        <div className="card-actions">
                          <button
                            className="icon-ellipsis"
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            title="Actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(menuOpen ? null : id);
                            }}
                          >
                            <i className="fas fa-ellipsis-h" />
                          </button>

                          {menuOpen && (
                            <div className="menu-pop" role="menu" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="menu-item"
                                role="menuitem"
                                onClick={() => product?._id && handleEditProduct(product._id)}
                                title="Edit product"
                                disabled={!product?._id}
                              >
                                <i className="fas fa-pen-to-square" />
                                Edit
                              </button>
                              <button
                                className="menu-item danger"
                                role="menuitem"
                                onClick={() => product?._id && handleDeleteProduct(product)}
                                title="Delete product"
                                disabled={!product?._id}
                              >
                                <i className="fas fa-trash-alt" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Gallery */}
                      {visibleImages.length > 0 && (
                        <div className="gallery">
                          {visibleImages.map((url, i) => {
                            const isLast = i === visibleImages.length - 1 && hiddenCount > 0;
                            return (
                              <a
                                key={i}
                                href={url}
                                data-fancybox={`gp-${product?._id || "item"}`}
                                data-caption={`${product?.brand || "Image"} ${i + 1}`}
                                aria-label="Open image"
                              >
                                <img src={url} alt={`img${i}`} loading="lazy" referrerPolicy="no-referrer" />
                                {isLast && (
                                  <div style={{
                                    position: "absolute", inset: 0, borderRadius: 10,
                                    background: "rgba(0,0,0,0.52)", backdropFilter: "blur(1px)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", fontWeight: 800, fontSize: 15,
                                  }}>
                                    +{hiddenCount}
                                  </div>
                                )}
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {/* Price */}
                      {price && (
                        <div style={{ marginBottom: 10 }}>
                          <span className="price-pill">
                            <i className="fas fa-tag" style={{ fontSize: 11 }} />
                            {price}
                          </span>
                        </div>
                      )}

                      {/* Description */}
                      {product?.description && (
                        <p className="desc">{product.description}</p>
                      )}

                      <div className="divider" />

                      {/* Meta */}
                      <div className="meta-stack">
                        {product?.category && (
                          <div className="meta-item">
                            <span className="meta-icon-label">
                              <i className="fas fa-tag" /> Category
                            </span>
                            <span className="meta-content">{product.category}</span>
                          </div>
                        )}
                        {product?.address && (
                          <div className="meta-item">
                            <span className="meta-icon-label">
                              <i className="fas fa-map-marker-alt" /> Address
                            </span>
                            <span className="meta-content">{product.address}</span>
                          </div>
                        )}
                        {contact && (
                          <div className="meta-item">
                            <span className="meta-icon-label">
                              <i className="fas fa-phone" /> Contact
                            </span>
                            <span className="meta-content">{contact}</span>
                          </div>
                        )}
                        {product?.dateTime && (
                          <div className="meta-item">
                            <span className="meta-icon-label">
                              <i className="fas fa-calendar" /> Date
                            </span>
                            <span className="meta-content">{formatDate(product.dateTime)}</span>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateGenuineProduct
        domain={domain}
        show={showCreateGenuineProductModal}
        handleClose={handleCloseCreateGenuineProductModal}
        handleFormSubmit={handleFormSubmit}
      />

      <EditGenuineProduct
        domain={domain}
        show={showEditGenuineProductModal}
        productId={editProductId}
        handleClose={handleCloseEditGenuineProductModal}
        handleFormSubmit={() => {
          handleFormSubmit();
          handleCloseEditGenuineProductModal();
        }}
      />

      <DeleteProductModal
        show={showDeleteModal}
        handleClose={() => { setShowDeleteModal(false); setDeleteProduct(null); }}
        productId={deleteProduct?._id}
        productName={deleteProduct?.brand}
        onDeleted={handleFormSubmit}
      />
    </>
  );
};

export default BuyGenuine;
